import type {
  ProAdminRefundDetailDto,
  ProAdminRefundExecutionStatus,
  ProAdminRefundListDto,
  ProAdminRefundListQuery,
  ProRefundRequestStatus,
} from "@dutyon/apis";
import { readRefundNotification } from "./notification-model.ts";
import { prepareRejection, readRejection } from "./reject-model.ts";
export const statuses: Record<ProRefundRequestStatus, string> = {
  REQUESTED: "접수됨",
  REVIEWING: "검토 중",
  APPROVED: "승인됨",
  PROCESSING: "처리 중",
  CONFIRMED: "환불 확정",
  REJECTED: "반려됨",
  RETRY_REQUIRED: "결과 확인 중",
};
export const executionStatuses: Record<ProAdminRefundExecutionStatus, string> = {
  WAITING: "전달 대기",
  PROCESSING: "처리 중",
  RECONCILING: "결과 조회 중",
  NEEDS_REVIEW: "운영자 확인 필요",
  CONFIRMED: "확정됨",
};
const id = (value: unknown) => typeof value === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(value);
export function readAdminRefundList(value: unknown, cursor?: string): ProAdminRefundListDto {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("목록을 확인할 수 없습니다.");
  const data = value as ProAdminRefundListDto;
  if (
    !Array.isArray(data.items) ||
    data.items.length > 20 ||
    (data.nextCursor !== null &&
      (!id(data.nextCursor) ||
        data.items.length !== 20 ||
        data.items[19]?.id !== data.nextCursor ||
        data.nextCursor === cursor))
  )
    throw new Error("목록을 확인할 수 없습니다.");
  const seen = new Set<string>();
  for (const row of data.items) {
    if (
      !row ||
      !id(row.id) ||
      seen.has(row.id) ||
      row.id === cursor ||
      !id(row.paymentId) ||
      !id(row.orderId) ||
      !id(row.inquiryId) ||
      !Object.keys(statuses).includes(row.status) ||
      !Number.isSafeInteger(row.revision) ||
      row.revision < 0 ||
      typeof row.requestedAt !== "string" ||
      !Number.isFinite(Date.parse(row.requestedAt)) ||
      new Date(row.requestedAt).toISOString() !== row.requestedAt
    )
      throw new Error("목록을 확인할 수 없습니다.");
    seen.add(row.id);
    if (
      row.executionStatus != null &&
      (!Object.keys(executionStatuses).includes(row.executionStatus) ||
        !["APPROVED", "PROCESSING", "RETRY_REQUIRED", "CONFIRMED"].includes(row.status) ||
        (row.executionStatus === "CONFIRMED"
          ? row.status !== "CONFIRMED"
          : row.status === "CONFIRMED"))
    )
      throw new Error("목록을 확인할 수 없습니다.");
  }
  return data;
}
export function prepareAdminRefundFilters(input: {
  status: string;
  executionStatus?: string;
  inquiryId: string;
  orderId: string;
  from: string;
  to: string;
}): ProAdminRefundListQuery {
  const result: ProAdminRefundListQuery = {};
  if (input.status) {
    if (!Object.keys(statuses).includes(input.status)) throw new Error("상태를 확인해 주세요.");
    result.status = input.status as ProRefundRequestStatus;
  }
  if (input.executionStatus) {
    if (!Object.keys(executionStatuses).includes(input.executionStatus))
      throw new Error("실행 상태를 확인해 주세요.");
    result.executionStatus = input.executionStatus as ProAdminRefundExecutionStatus;
  }
  for (const field of ["inquiryId", "orderId"] as const) {
    const value = input[field].trim();
    if (value) {
      if (!id(value)) throw new Error("문의·주문 번호를 확인해 주세요.");
      result[field] = value;
    }
  }
  for (const field of ["from", "to"] as const) {
    const value = input[field];
    if (value) {
      if (
        !/^20\d\d-\d{2}-\d{2}$/.test(value) ||
        !Number.isFinite(Date.parse(value)) ||
        new Date(value).toISOString().slice(0, 10) !== value
      )
        throw new Error("접수일을 확인해 주세요.");
      result[field] = value;
    }
  }
  if (result.from && result.to && result.from > result.to)
    throw new Error("접수 종료일은 시작일 이후여야 합니다.");
  return result;
}

export function readAdminRefundDetail(value: unknown, expectedId: string): ProAdminRefundDetailDto {
  const fail = () => {
    throw new Error("환불 상세를 확인할 수 없습니다.");
  };
  if (!value || typeof value !== "object" || Array.isArray(value)) return fail();
  const data = value as ProAdminRefundDetailDto;
  const request = data.request,
    payment = data.payment;
  if (!request || !payment || request.id !== expectedId) return fail();
  readAdminRefundList({ items: [{ ...request, orderId: payment.orderId }], nextCursor: null });
  const money = (amount: unknown) => Number.isSafeInteger(amount) && Number(amount) >= 0;
  if (
    request.paymentId !== payment.id ||
    !id(payment.id) ||
    typeof request.reason !== "string" ||
    request.reason.trim().length < 1 ||
    request.reason.length > 1000 ||
    !money(request.paymentAmount) ||
    !money(request.availableAmount) ||
    request.availableAmount > request.paymentAmount ||
    !money(payment.amount) ||
    !money(payment.refundedAmount) ||
    !money(payment.availableAmount) ||
    payment.refundedAmount > payment.amount ||
    payment.availableAmount !== payment.amount - payment.refundedAmount ||
    request.paymentAmount !== payment.amount ||
    request.currency !== "KRW" ||
    payment.currency !== "KRW" ||
    !["PENDING", "UNKNOWN", "PAID", "FAILED", "PARTIALLY_REFUNDED", "REFUNDED"].includes(
      payment.status,
    ) ||
    (payment.paidAt !== null &&
      (typeof payment.paidAt !== "string" ||
        !Number.isFinite(Date.parse(payment.paidAt)) ||
        new Date(payment.paidAt).toISOString() !== payment.paidAt))
  )
    return fail();
  if (
    data.inquiry !== null &&
    (!data.inquiry ||
      data.inquiry.id !== request.inquiryId ||
      typeof data.inquiry.title !== "string" ||
      typeof data.inquiry.content !== "string" ||
      (data.inquiry.answer !== null && typeof data.inquiry.answer !== "string"))
  )
    return fail();
  const iso = (date: unknown): date is string =>
    typeof date === "string" &&
    Number.isFinite(Date.parse(date)) &&
    new Date(date).toISOString() === date;
  const order = data.order,
    entitlement = data.entitlement,
    generation = data.generation;
  if (
    !order ||
    !money(order.baseAmount) ||
    !Number.isSafeInteger(order.discountBps) ||
    order.discountBps < 0 ||
    order.discountBps > 10000 ||
    !["MONTH", "YEAR"].includes(order.interval) ||
    (order.periodStart !== null && !iso(order.periodStart)) ||
    (order.periodEnd !== null && !iso(order.periodEnd)) ||
    (order.periodStart && order.periodEnd && order.periodStart >= order.periodEnd)
  )
    return fail();
  if (
    entitlement !== null &&
    (!entitlement ||
      !iso(entitlement.startsAt) ||
      !iso(entitlement.endsAt) ||
      entitlement.startsAt >= entitlement.endsAt ||
      (entitlement.revokedAt !== null && !iso(entitlement.revokedAt)))
  )
    return fail();
  if (
    generation !== null &&
    (!generation ||
      !id(generation.id) ||
      !["QUEUED", "RUNNING", "SUCCEEDED", "FAILED", "CANCELED", "EXPIRED"].includes(
        generation.status,
      ) ||
      !iso(generation.createdAt) ||
      (generation.finishedAt !== null && !iso(generation.finishedAt)) ||
      (generation.errorCode !== null && typeof generation.errorCode !== "string") ||
      (generation.usageState !== null &&
        !["RESERVED", "CONSUMED", "RELEASED"].includes(generation.usageState)))
  )
    return fail();
  if (data.rejection !== null) {
    if (!data.rejection || request.status !== "REJECTED" || request.revision < 1) return fail();
    readRejection(
      data.rejection,
      request.id,
      prepareRejection(data.rejection.reason, request.revision - 1),
    );
  }
  if (data.approval !== null) {
    const approval = data.approval;
    if (
      !approval ||
      !["APPROVED", "PROCESSING", "CONFIRMED", "RETRY_REQUIRED"].includes(request.status) ||
      !id(approval.id) ||
      !id(approval.intentId) ||
      approval.requestId !== request.id ||
      approval.status !== "APPROVED" ||
      !Number.isSafeInteger(approval.revision) ||
      approval.revision < 1 ||
      approval.revision > request.revision ||
      (request.status === "APPROVED" && approval.revision !== request.revision) ||
      typeof approval.reason !== "string" ||
      approval.reason !== approval.reason.trim() ||
      !approval.reason ||
      approval.reason.length > 1000 ||
      !money(approval.amount) ||
      approval.amount < 1 ||
      approval.amount > request.availableAmount ||
      approval.currency !== "KRW" ||
      !iso(approval.decidedAt)
    )
      return fail();
  }
  const confirmation = data.confirmation;
  if (confirmation != null) {
    if (
      !data.approval ||
      !id(confirmation.id) ||
      confirmation.requestId !== request.id ||
      confirmation.intentId !== data.approval.intentId ||
      confirmation.paymentId !== data.payment.id ||
      request.status !== "CONFIRMED" ||
      confirmation.revision !== request.revision ||
      !money(confirmation.amount) ||
      confirmation.amount < 1 ||
      confirmation.amount !== data.approval.amount ||
      confirmation.currency !== "KRW" ||
      !iso(confirmation.refundedAt) ||
      !iso(confirmation.confirmedAt) ||
      Date.parse(confirmation.refundedAt) > Date.parse(confirmation.confirmedAt) + 300000 ||
      data.payment.status !== "REFUNDED" ||
      data.payment.refundedAmount !== data.payment.amount ||
      data.entitlement?.revokedAt !== confirmation.confirmedAt
    )
      return fail();
  }
  const execution = data.execution;
  if (
    execution != null &&
    (!data.approval ||
      execution.intentId !== data.approval.intentId ||
      !Object.keys(executionStatuses).includes(execution.status) ||
      !Number.isSafeInteger(execution.attempt) ||
      execution.attempt < 0 ||
      execution.attempt > 2147483647 ||
      !iso(execution.updatedAt) ||
      (execution.nextAttemptAt !== null && !iso(execution.nextAttemptAt)) ||
      (execution.issue !== null &&
        !["UNCERTAIN", "PROVIDER_FAILED", "RECONCILIATION_REQUIRED"].includes(execution.issue)) ||
      (execution.status === "WAITING" && execution.attempt !== 0) ||
      (["PROCESSING", "RECONCILING", "NEEDS_REVIEW"].includes(execution.status) &&
        execution.attempt < 1) ||
      (["WAITING", "RECONCILING"].includes(execution.status)
        ? execution.nextAttemptAt === null
        : execution.nextAttemptAt !== null) ||
      (execution.status === "CONFIRMED"
        ? !confirmation || execution.issue !== null || request.status !== "CONFIRMED"
        : !!confirmation || request.status === "CONFIRMED"))
  )
    return fail();
  readRefundNotification(data.notification, request.status);
  return data;
}
